import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import "../styles/about.css";

// Founder videos + bios
const founders = [
  {
    name: "Alec Lance",
    role: "Co-Founder, Synergistic Intelligence",
    bioLead: "Came through curiosity.",
    bio: "There was no dramatic collapse. Just a feeling that the current path wasn't sustainable. He'd been counting on a promotion that was denied. Around the same time, he found out he was going to be a father. Something in him knew the man he'd been striving to become couldn't ever be the dad he wanted to be. He didn't wait for the collapse. He followed the quiet signal while everything, on paper, still looked fine.",
    embedSrc:
      "https://www.youtube.com/embed/pky1L9eR0Qs?start=3&rel=0&modestbranding=1",
  },
  {
    name: "Stan Cross",
    role: "Co-Founder, Synergistic Intelligence",
    bioLead: "Came through surrender.",
    bio: "For 23 years, he'd led a $300 million a year company — then watched it slowly fall apart from the inside, despite everything he did to save it. When it was finally gone, sold in the eleventh hour to stave off bankruptcy, so was the version of himself he'd lived so confidently inside for decades. He remembers lying on his bed with one clear thought: I don't know who I am without the company. Only after everything he'd held onto was gone did something open inside him. Ideas and truths he'd never been able to hear before suddenly felt like the wisdom he'd needed all along. He's lived on the other side of that opening more or less ever since.",
    embedSrc:
      "https://www.youtube.com/embed/j6FNN_fcW4Q?start=1&rel=0&modestbranding=1",
  },
];

// Shared fade-up variant
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

function FounderCard({ name, role, bioLead, bio, embedSrc }) {
  return (
    <motion.div
      className="about-founder-card"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="about-founder-video-wrap">
        <div className="about-founder-video-glow" aria-hidden="true" />
        <div className="about-founder-video-overlay" aria-hidden="true" />
        <iframe
          src={embedSrc}
          title={`${name} — Co-Founder`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      <div className="about-founder-info">
        <div className="about-founder-name">{name}</div>
        <div className="about-founder-role">{role}</div>
        <p className="about-founder-bio">
          <strong className="about-founder-bio-lead">{bioLead}</strong>{" "}
          {bio}
        </p>
      </div>
    </motion.div>
  );
}

export default function About() {
  return (
    <>
      {/* ── HERO ── */}
      <motion.section
        className="about-hero"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <p className="section-label">About Synergistic Intelligence</p>
        <h1>
          We built this
          <br />
          <em>because we needed it.</em>
        </h1>
        <p className="about-hero-lead">
          We spent years searching outward — optimizing, achieving,
          resetting. And still, something felt just out of reach.
          <br />
          <br />
          <strong>
            Synergistic Intelligence began the day we started looking in a
            different direction.
          </strong>
        </p>
      </motion.section>

      {/* ── RECOGNITION BAND (the doorway) ── */}
      <motion.div
        className="about-recognition-band"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.8 }}
      >
        <p>
          We help people recognize what they already know — and cross the
          threshold it opens. On the other side, something changes: people
          stop leading from fear, and start leading from what's real. That's
          the whole of our work.
        </p>
      </motion.div>

      {/* ── ORIGIN STORY ── */}
      <motion.section
        className="about-origin"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeUp}
      >
        <div className="about-origin-left">
          <h1 className="section-label">How it began</h1>
          <h2 className="section-title">
            It started with
            <br />
            <em>a simple check-in.</em>
          </h2>
          <p>
            <br />
            After helping Alec buy a home, Stan sensed something deeper was
            stirring beneath the surface. Alec wasn't struggling with real
            estate — he was wrestling with meaning.
          </p>
          <p>
            Stan recommended a book: <em>The Fifth Discipline</em> — and
            specifically, the section on Personal Mastery. One passage hit
            Alec hard.
          </p>
          <p>
            What followed was a series of rich conversations and a small
            group exploring purpose, awareness, and alignment. In that
            space, something deeper emerged: flow.
          </p>
          <p>
            As their dialogue deepened, so did their clarity. They noticed
            how presence and inner awareness fueled creativity and insight —
            and how much had been waiting, unnoticed, underneath the
            striving.
          </p>
          <p className="about-origin-closing">
            <strong>That was the seed of Synergistic Intelligence.</strong>
          </p>
          <p className="about-origin-tagline">
            SI was never meant to be just a company. It was a response to a
            call — to help people cross from fear to flow. That journey is
            still unfolding.
          </p>
          <div className="about-origin-quote-block about-origin-quote-block--simple">
            <blockquote>
              What I was searching for — for years — was in me the whole
              time. I just had to get out of my own way.
            </blockquote>
            <cite>
              Alec Lance
              <span>Co-Founder</span>
            </cite>
          </div>
        </div>

        <div className="about-origin-right">
          <div className="about-origin-photo">
            <img
              src={process.env.PUBLIC_URL + "/images/stan_and_alec.jpeg"}
              alt="Alec Lance and Stan Cross"
            />
          </div>
          <div className="about-origin-quote-block">
            <span className="about-origin-quote-mark">"</span>
            <blockquote>
              This is the true joy in life, being used for a purpose
              recognized by yourself as a mighty one… being a force of
              nature instead of a feverish, selfish little clod of ailments
              and grievances.
            </blockquote>
            <cite>
              George Bernard Shaw
              <span>The passage that cracked it open for Alec</span>
            </cite>
          </div>
          <div className="about-origin-note">
            <p>
              From <em>The Fifth Discipline</em> by Peter Senge — the book
              Stan handed Alec that started everything.
            </p>
          </div>
        </div>
      </motion.section>

      {/* ── FOUNDERS ── */}
      <section className="about-founders">
        <p className="section-label">The people behind the work</p>
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          Two ways in.
        </motion.h2>
        <motion.p
          className="about-founders-intro"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          There are two doors into this work. We each came through a
          different one — and neither is the right one. Stan reminds us not
          to underestimate the power of letting go. Alec reminds us not to
          underestimate the power of quiet recognition — of catching the
          whisper before the crisis has to come. Together, they see more
          than either could alone.
        </motion.p>

        <div className="about-founders-grid">
          {founders.map((founder) => (
            <FounderCard key={founder.name} {...founder} />
          ))}
        </div>
      </section>

      {/* ── GUIDING INQUIRY (what we're devoted to) ── */}
      <motion.section
        className="about-premise"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
      >
        <div className="about-premise-inner about-premise-inner--center">
          <p className="section-label">What we're devoted to</p>
          <p className="about-premise-body">
            Underneath all of it, we're devoted to a single question.
          </p>
          <h2 className="section-title about-premise-question">
            How do human beings awaken
            <br />
            <em>to greater coherence?</em>
          </h2>
          <p className="about-premise-body about-premise-body--wide">
            Not a technique. Not a framework. A question we keep living our
            way toward — with the people who feel it too.
          </p>
          <a href="/manifesto" className="about-manifesto-link">
            Read our founding vision &nbsp;·&nbsp; A Synergistic
            Intelligence Manifesto
          </a>
        </div>
      </motion.section>

      {/* ── CONTRIBUTION (what we actually do) ── */}
      <motion.section
        className="about-premise about-premise--alt"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
      >
        <div className="about-premise-inner">
          <p className="section-label">What we actually do</p>
          <h2 className="section-title">We help people see the pattern.</h2>
          <p className="about-premise-body">
            Beneath the noise, the same few patterns quietly shape how a
            person meets their life — how they hold uncertainty, control,
            fear, and change. Most of us can't see our own.
          </p>
          <p className="about-premise-body">
            <strong>We help people recognize theirs</strong> — so they can
            respond to life with more coherence, and less struggle. That's
            the work, in every form it takes.
          </p>
        </div>
      </motion.section>

      {/* ── MID-PAGE ASSESSMENT CTA ── */}
      <div className="about-assessment-band">
        <p className="about-assessment-band-label">
          Wondering where you fit in this journey?
        </p>
        <p className="about-assessment-band-body">
          The Flow Identity Assessment takes 10 minutes and shows you
          exactly where to start — and what becomes possible from there.
        </p>
        <div className="about-assessment-band-group">
          <Link to="/assessment" className="btn-secondary-about">
            Take the Flow Identity Assessment →
          </Link>
          <span className="about-assessment-band-note">
            Free · 10 minutes · Personalized results
          </span>
        </div>
      </div>

      {/* ── MANIFESTO CTA ── */}
      <motion.div
        className="about-manifesto-cta"
        id="manifesto"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.4 }}
        variants={fadeUp}
      >
        <div className="about-mcta-left">
          <h2>Want to go deeper?</h2>
          <p>
            For those who want the full philosophical picture — our
            founding vision, our core premises, and the world we believe is
            possible — we wrote it down. It's not required reading. But if
            you're that kind of person, it's waiting for you.
          </p>
        </div>
        <div className="about-mcta-right">
          <a href="/manifesto" className="btn-primary-about">
            Read the Founding Vision
          </a>
        </div>
      </motion.div>

      {/* ── EARNED CLOSING LINE ── */}
      <div className="about-closing-line">
        <p className="about-closing-line-lead">
          After all of it — the searching, the striving, the reaching
          outward —
        </p>
        <p className="about-closing-line-main">
          You already have what you're looking for.
        </p>
      </div>

      {/* ── FINAL CTA ── */}
      <section className="about-final-cta">
        <h2>Curious where you actually are?</h2>
        <p>
          The Flow Identity Assessment takes ten minutes, costs nothing, and
          meets you exactly where you are. It's the honest first step — and
          everything else follows from it.
        </p>
        <div className="about-final-cta-group">
          <Link to="/assessment" className="btn-light-about">
            Take the Flow Identity Assessment →
          </Link>
        </div>
      </section>
    </>
  );
}